import { notification } from 'antd';
import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { UserRole } from '../../../enum';
import { BASE_ENDPOINT, US_REGION } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { abortAllRequests } from '../../../service/axiosAbort.service';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { getCurrentUser, getRememberedEmail, saveRememberedEmail } from './login.helper';

const { setRaygunUser, resetRaygunUser, logError } = RaygunErrorHandlerService();

export class AuthResult {
  country: string | null = null;
  full_name: string | null = null;
  org_name: string | null = null;
  token: string | null = null;
  use_actions: boolean = false;
  use_badge: boolean = false;
  use_bam_17_trend_graph: boolean = false;
  use_phq_9_trend_graph: boolean = false;
  use_client_journal_selection: boolean = false;
  use_cravings_quick_checkin: boolean = false;
  use_indicator_graph_card: boolean = false;
  use_location: boolean = false;
  use_mood: boolean = false;
  use_multi_product_clients: boolean = false;
  use_risk_calendar_graph: boolean = false;
  use_status_time_graph: boolean = false;
  use_risk_indicator_graph: boolean = false;
  use_score_column: boolean = false;
  use_sentiment: boolean = false;
  use_si: boolean = false;
  use_sms: boolean = false;
  use_streak_component: boolean = false;
  use_streak_component_title: boolean = false;
  use_text_entry: boolean = false;
  use_insurance_carrier: boolean = false;
  use_apa_features: boolean = false;
  use_risk_nomenclature: boolean = false;
  use_callback: boolean = false;
  use_client_program: boolean = false;
  use_client_type: boolean = false;
  use_clinician_dashboard: boolean = false;
  use_always_available_products: boolean = false;
  use_scheduler: boolean = false;
  use_qr_code: boolean = false;
  use_critical_risk: boolean = false;
}

export type Login = {
  email: string;
  password: string;
};

export class CurrentUser {
  role: UserRole | null = null;
  user_id: string | null = null;
  organization_id: string | null = null;
  exp: number | null = null;
  region: string | null = null;
}

type AuthSliceType = {
  authResult: AuthResult;
  authResult2: AuthResult;
  loadingLogin: boolean;
  currentUser: CurrentUser;
  loadingForgotPassword: boolean;
  redirectPath: string | undefined;
};

export const initialState: AuthSliceType = {
  authResult: new AuthResult(),
  authResult2: new AuthResult(),
  loadingLogin: false,
  currentUser: new CurrentUser(),
  loadingForgotPassword: false,
  redirectPath: undefined,
};

export const login = createAsyncThunk<AuthResult, Login, AsyncThunkConfig>('auth/login', async (login, thunkAPI) => {
  try {
    const endpoint = 'v2_login';
    const [resp1, resp2] = await Promise.allSettled([
      axios.post(endpoint, login, { baseURL: BASE_ENDPOINT.CA }) as unknown as [AuthResult],
      axios.post(endpoint, login, { baseURL: BASE_ENDPOINT.US }) as unknown as [AuthResult],
    ]);
    if (resp1.status === 'fulfilled' && resp1.value[0].token) {
      return resp1.value[0];
    } else if (resp2.status === 'fulfilled' && resp2.value[0].token) {
      return resp2.value[0];
    } else if (resp1.status === 'rejected') {
      throw resp1.reason;
    } else if (resp2.status === 'rejected') {
      throw resp2.reason;
    } else {
      return new AuthResult();
    }
  } catch (e) {
    logError(e, ['authSlice', 'login']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const login2 = createAsyncThunk<AuthResult, Login, AsyncThunkConfig>(
  'auth/login2',
  async (login: Login, thunkAPI) => {
    try {
      const currentUser = thunkAPI.getState().authSlice.currentUser;
      const baseURL = currentUser.region === US_REGION ? BASE_ENDPOINT.CA : BASE_ENDPOINT.US;
      const response = (await axios.post('v2_login', login, { baseURL })) as [AuthResult];
      return response?.[0] ?? new AuthResult();
    } catch (e) {
      logError(e, ['authSlice', 'login2']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const refreshToken = createAsyncThunk<AuthResult, undefined, AsyncThunkConfig>(
  'auth/refreshToken',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v2_token_refresh')) as [AuthResult];
      if (response?.[0].token) {
        return response[0];
      } else {
        notification.error({
          title: 'Token expired',
          description: 'Please sign in again.',
        });
        thunkAPI.dispatch(logout());
        return thunkAPI.rejectWithValue((response as unknown as AxiosResponse<ErrorMessage>).data);
      }
    } catch (e) {
      logError(e, ['authSlice', 'refreshToken']);
      thunkAPI.dispatch(logout());
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendForgotPasswordEmail = createAsyncThunk<boolean, string, AsyncThunkConfig>(
  'auth/sendForgotPasswordEmail',
  async (email: string, thunkAPI) => {
    try {
      const endpoint = 'forgot_password_admin';
      const [resp1, resp2] = await Promise.allSettled([
        axios.post(endpoint, { email }, { baseURL: BASE_ENDPOINT.CA }),
        axios.post(endpoint, { email }, { baseURL: BASE_ENDPOINT.US }),
      ]);
      if (resp1.status === 'fulfilled') {
        return true;
      } else if (resp2.status === 'fulfilled') {
        return true;
      } else if (resp1.status === 'rejected') {
        throw resp1.reason;
      } else if (resp2.status === 'rejected') {
        throw resp2.reason;
      } else {
        return false;
      }
    } catch (e) {
      logError(e, ['authSlice', 'sendForgotPasswordEmail']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const logout = createAsyncThunk<undefined, undefined, AsyncThunkConfig>('auth/logout', async (_, thunkAPI) => {
  try {
    abortAllRequests();
    resetRaygunUser();
    const rememberedEmail = await getRememberedEmail();
    localStorage.clear();
    if (rememberedEmail) {
      await saveRememberedEmail(rememberedEmail);
    }
    thunkAPI.dispatch({ type: 'LOG_OUT' });
  } catch (e) {
    logError(e, ['authSlice', 'logout']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    restoreAuthState: (state, action) => {
      return { ...state, ...action.payload };
    },
    setRedirectPath: (state, action: PayloadAction<string | undefined>) => {
      state.redirectPath = action.payload;
    },
    clearRedirectPath: (state) => {
      state.redirectPath = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state, _action) => {
        state.loadingLogin = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        const { token } = action.payload;
        if (token) {
          state.authResult = action.payload;
          state.loadingLogin = false;
          state.currentUser = getCurrentUser(token);
          setRaygunUser({
            organization_id: state.currentUser.organization_id,
            user_id: state.currentUser.user_id,
            role: state.currentUser.role,
            region: state.currentUser.region,
          });
        } else {
          state.authResult = new AuthResult();
          state.loadingLogin = false;
        }
      })
      .addCase(login.rejected, (state, _action) => {
        state.loadingLogin = false;
      })
      .addCase(login2.pending, (state, _action) => {
        state.loadingLogin = true;
      })
      .addCase(login2.fulfilled, (state, action) => {
        const { token } = action.payload;
        if (token) {
          state.authResult2 = action.payload;
          state.loadingLogin = false;
        } else {
          state.authResult2 = new AuthResult();
          state.loadingLogin = false;
        }
      })
      .addCase(login2.rejected, (state, _action) => {
        state.loadingLogin = false;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        const { token } = action.payload;
        if (token) {
          state.authResult = action.payload;
          state.currentUser = getCurrentUser(token);
          setRaygunUser({
            organization_id: state.currentUser.organization_id,
            user_id: state.currentUser.user_id,
            role: state.currentUser.role,
            region: state.currentUser.region,
          });
        } else {
          state.authResult = new AuthResult();
        }
      })
      .addCase(sendForgotPasswordEmail.pending, (state, _action) => {
        state.loadingForgotPassword = true;
      })
      .addCase(sendForgotPasswordEmail.fulfilled, (state, _action) => {
        state.loadingForgotPassword = false;
      })
      .addCase(sendForgotPasswordEmail.rejected, (state, _action) => {
        state.loadingForgotPassword = false;
      });
  },
});

export const { restoreAuthState, setRedirectPath, clearRedirectPath } = authSlice.actions;
